import type { TextureTier } from './manifest';

/**
 * Whether the current browser appears to be a mobile device. UA-sniffed —
 * imperfect but low-false-positive, and wrong calls just mean a smaller
 * texture loads (no functional breakage).
 */
export function isMobileDevice(): boolean {
  if (typeof navigator === 'undefined') return false;
  return /iPhone|iPad|iPod|Android|Mobile/i.test(navigator.userAgent);
}

/** Whether the user has enabled browser data-saver mode. */
export function prefersSaveData(): boolean {
  if (typeof navigator === 'undefined') return false;
  const conn = (navigator as unknown as { connection?: { saveData?: boolean } }).connection;
  return conn?.saveData === true;
}

/**
 * Pick the tier to load on FIRST PAINT. Conservative by design: snappier
 * initial render beats higher pixel density when the user hasn't even
 * looked at the globe yet. Higher tiers stream in progressively as the
 * user zooms in (see MapEngine.maybeUpgradeTier).
 *
 * - Save-data → small (respect the explicit signal).
 * - Mobile → small (assume bandwidth-constrained even on capable GPUs).
 * - Desktop with a usable GPU → medium. Always.
 * - GPU < 4096 → small fallback (medium is 4096 wide; safer to stay under).
 *
 * WebGL's MAX_TEXTURE_SIZE cap is the key constraint: any texture whose
 * LARGER dimension exceeds it silently fails to upload. Our tier heights
 * are half their widths, so we gate on the width.
 */
export function selectFirstPaintTier(gpuMaxTextureSize: number): TextureTier {
  if (prefersSaveData()) return 'small';
  if (isMobileDevice()) return 'small';
  if (gpuMaxTextureSize >= 4096) return 'medium';
  return 'small';
}

/**
 * The HIGHEST tier the device can safely upload. Used by the progressive
 * upgrade ladder to know when to stop chasing higher resolutions on zoom.
 */
export function maxTierForGpu(gpuMaxTextureSize: number): TextureTier {
  if (gpuMaxTextureSize >= 16384) return 'xl';
  if (gpuMaxTextureSize >= 8192) return 'large';
  if (gpuMaxTextureSize >= 4096) return 'medium';
  return 'small';
}
